<template>
  <div :class="`basic-contact contact-form ${getContactFormErrorClasses()} ${getContentClass()}`">
    <!-- Contact dialog -->
    <div class="contact-form__content">
      <h1>{{ getTitle() }}</h1>

      <div class="contact-form__form">
        <div class="contact-form__office">
          <div class="contact-form__office-header">{{ office.name }} -</div>
          <div class="contact-form__office-title">
            <span>{{ company.name }}</span>
          </div>
        </div>

        <div class="contact-form__recipient">
          <div class="contact-form__recipient-header">Vastaanottaja:</div>
          <div class="contact-form__recipient-name">
            {{ agent.name }}
            <div class="contact-form__recipient-title">
              <span>{{ agent.title }}</span>
            </div>
          </div>
        </div>

        <!-- Form basic contents -->
        <div class="form-tabs">
          <div
            v-for="(type, index) in requestTypes"
            :key="`request-type-${index}`"
            class="form-tabs__option"
            :class="{ selected: formData.requestType === type.id }"
            @click="selectActionType(type.id)"
          >
            {{ type.action }}
          </div>
        </div>

        <div class="form-field">
          <label for="message" class="label">Viesti</label>
          <textarea
            data-hj-masked
            id="message"
            class="input message"
            rows="8"
            v-model="formData.message"
            v-on:focus="inputFocus($event)"
            v-on:keyup.once="detectFormStart()"
          ></textarea>
        </div>

        <div class="form-row">
          <div class="form-field">
            <label for="first-name" class="label">Etunimi</label>
            <input
              data-hj-masked
              id="first-name"
              type="text"
              class="input first-name"
              v-model="formData.firstName"
              v-on:focus="inputFocus($event)"
              v-on:keyup.once="detectFormStart()"
            />
          </div>
          <div class="form-field">
            <label for="last-name" class="label">Sukunimi</label>
            <input
              data-hj-masked
              id="last-name"
              type="text"
              class="input last-name"
              v-model="formData.lastName"
              v-on:focus="inputFocus($event)"
              v-on:keyup.once="detectFormStart()"
            />
          </div>
        </div>

        <div class="form-field">
          <span class="label">Sähköposti</span>
          <input
            data-hj-masked
            type="email"
            class="input email"
            v-model="formData.email"
            v-on:focus="inputFocus($event)"
            v-on:keyup.once="detectFormStart()"
          />
        </div>

        <div class="form-row">
          <div class="form-field">
            <span class="label">Puhelinnumero</span>
            <input
              data-hj-masked
              type="tel"
              class="input telephone"
              v-model="formData.phone"
              v-on:focus="inputFocus($event)"
              v-on:keyup.once="detectFormStart()"
            />
          </div>
        </div>

        <div class="form-checkbox form-field">
          <input
            class="checkbox-input"
            type="checkbox"
            value="phone"
            :id="checkboxId('phone')"
            v-model="formData.contactTypes"
            v-on:change.once="detectFormStart()"
          />
          <label :for="checkboxId('phone')">Haluan, että minuun ollaan yhteydessä puhelimitse</label>
        </div>

        <div class="form-field" v-if="formData.contactTypes.includes('phone')">
          <span class="label">Toivomani aika yhteydenotolle</span>
          <input
            data-hj-masked
            type="text"
            class="input"
            v-model="formData.contactTime"
            v-on:focus="inputFocus($event)"
            v-on:keyup.once="detectFormStart()"
          />
        </div>

        <ul class="validation-errors" v-show="errors">
          <li v-for="(error, index) in errors" :key="`error-${index}`">{{ error.message }}</li>
        </ul>

        <div>
          <div class="submit-error" v-if="errorMessage">{{ errorMessage }}</div>
          <div class="submit-success" v-if="resultMessage">{{ resultMessage }}</div>
        </div>

        <div class="form-actions">
          <form-button
            text="Peruuta"
            theme=""
            :disabled="sending"
            @click.native="closeForm('cancel')"
            tabindex="0"
          ></form-button>
          <form-button
            tabindex="0"
            text="Lähetä viesti"
            theme="primary"
            :disabled="sending"
            :inProgress="sending"
            @click.native="postContactForm()"
          ></form-button>
        </div>
      </div>

      <!-- Form success/failure contents -->
      <div class="contact-form__thanks">
        <p>{{ getMessage() }}</p>
        <form-button theme="primary" text="Sulje" @click.native="closeForm()"></form-button>
      </div>
    </div>
  </div>
</template>

<script>
import * as actions from '@/app/store/action-types';
import validationUtils from '@/utils/validation';
import { mapGetters } from 'vuex';
import FormInput from '@/app/components/form-input/form-input.vue';
import formUtils from '@/utils/form-utils';
import config from '@/config';
import utils from '@/app/utils/utils';
import * as _ from 'lodash-es';
import { defineAsyncComponent } from 'vue';
import logger from '@/app/logger';

export default {
  name: 'basic-contact',
  props: {
    embeddedForm: {
      type: Boolean,
      required: false,
    },
    level: {
      type: String,
      required: true,
    },
    agent: {
      type: Object,
      required: false,
    },
    company: {
      type: Object,
      required: true,
    },
    office: {
      type: Object,
      required: true,
    },
    analytics: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    utils: utils,
    requestTypes: utils.contactFormStrings,
    id: '',
    availableContactTypes: [
      { id: 'email', name: 'Sähköpostilla' },
      { id: 'phone', name: 'Puhelimitse' },
    ],
    formFillingStarted: false,
    sending: false,
    formData: {
      email: '',
      firstName: '',
      lastName: '',
      requestType: '',
      message: '',
      phone: '',
      contactTypes: [],
    },
    errors: [],
    result: {
      visible: false,
      title: null,
      message: null,
    },
    actionMapping: {
      selling: 'olen-myymassa',
      buying: 'olen-ostamassa',
      leaseWanted: 'haluan-vuokrata',
      leaseOffered: 'haluan-antaa-vuokralle',
      office: 'muu-asia',
    },
  }),
  methods: {
    selectActionType(id) {
      this.formData.requestType = id;
      this.detectFormStart();
    },
    detectFormStart() {
      if (!this.formFillingStarted) {
        void this.$store.dispatch(actions.analytics.track, config.analytics.eventTypes.form.start);
        this.formFillingStarted = true;
      }
    },
    inputFocus(event) {
      utils.inputFocusOnAndroid(event);
    },
    closeForm(message) {
      this.$bus.$emit('closeDialogue');

      this.formFillingStarted = false;
      this.resetForm();

      this.sendClosingAnalytics(message);

      if (this.$route.query.contact) {
        void this.$router.push({ query: { contact: false } });
      }
    },
    sendClosingAnalytics(message) {
      if (message && message.length) {
        void this.$store.dispatch(actions.analytics.append, { eVar67: message });
        void this.$store.dispatch(actions.analytics.track, config.analytics.eventTypes.form.close);
      }
      void this.$store.dispatch(actions.analytics.append, {
        eVar21: undefined,
        eVar22: undefined,
        eVar56: undefined,
        eVar57: undefined,
        eVar67: undefined,
        eVar75: undefined,
      });
    },
    resetForm() {
      this.$store.dispatch(actions.RESET_CONTACT_FORM_RESULTS);
      this.result.visible = false;
      this.result.title = null;
      this.result.message = null;
      this.sending = false;
      this.errors = [];
      this.formData.email = '';
      this.formData.phone = '';
      this.formData.firstName = '';
      this.formData.lastName = '';
      this.formData.requestType = this.requestTypes[0].id;
      this.formData.message = '';
      this.formData.contactTypes = [];
    },
    showFormResult() {
      this.result.visible = true;
      this.result.title = this.errorMessage ? 'Virhe lomakkeen lähetyksessä' : 'Kiitos yhteydenotosta!';
      this.result.message = this.errorMessage ? this.errorMessage : 'Olemme sinuun lähiaikoina yhteydessä';
    },
    getContactFormErrorClasses() {
      let result = '';
      this.errors.forEach((error) => {
        result += ` ${error.className}`;
      });
      return result;
    },
    getContentClass() {
      return this.result.visible ? 'show-results' : '';
    },
    getTitle() {
      return this.result.title || 'Ota yhteyttä';
    },
    getMessage() {
      return this.errorMessage || 'Olemme sinuun lähiaikoina yhteydessä.';
    },
    postContactForm() {
      this.$store.dispatch(actions.RESET_CONTACT_FORM_RESULTS);
      this.validate(this.formData);

      if (!this.errors.length) {
        this.sending = true;
        const payload = this.getContactPayload(this.formData);

        const that = this;
        grecaptcha.ready(function () {
          grecaptcha
            .execute('6Lcs9awZAAAAAIg5qOpFkbrTFWfXJZ-bI5TQkXbs', {
              action: 'send_basic_contact_request',
            })
            .then(function (token) {
              payload.token = token;
              payload.action = 'send_basic_contact_request';
              setTimeout(() => {
                that.$store.dispatch(actions.POST_BASIC_CONTACT_FORM, payload);
                that.sending = false;
              }, 1000);
            })
            .catch((e) => {
              logger.error(e);
            });
        });
      }
    },
    getContactPayload(form) {
      const actors = { buying: 'buyer', selling: 'seller' };

      const result = {
        agentId: this.agent.agentId,
        companyId: this.company.id,
        officeId: this.office.id,
        level: this.level,
        firstName: form.firstName,
        lastName: form.lastName,
        phone: utils.stripWhiteSpaces(form.phone),
        email: form.email,
        requestType: 'agent',
        actor: actors[form.requestType] || 'other',
        message: formUtils.formatMessage(form),
      };

      Object.keys(result).forEach((key) => {
        if (!result[key]) {
          delete result[key];
        }
      });

      return result;
    },
    validate() {
      this.errors = [];

      if (!this.formData.email.length && !this.formData.phone.length) {
        this.errors.push(this.validationErrors.contact);
      }

      if (!this.formData.message.length) {
        this.errors.push(this.validationErrors.messageContent);
      }

      if (!this.formData.firstName.length || !this.formData.lastName.length) {
        this.errors.push(this.validationErrors.name);
      }

      if (this.formData.email.length) {
        if (!validationUtils.validateEmail(this.formData.email)) {
          this.errors.push(this.validationErrors.email);
        }
      }

      if (this.formData.phone.length) {
        if (!validationUtils.validateTelephone(this.formData.phone)) {
          this.errors.push(this.validationErrors.phone);
        }
      }
    },
    checkboxId(n) {
      return `${this.id}-chk-${n}`;
    },
    sendContactAnalytics(analytics) {
      void this.$store.dispatch(actions.analytics.append, {
        eVar21: analytics.eVar21,
        eVar22: analytics.eVar22,
      });

      if (!this.embeddedForm) {
        void this.$store.dispatch(actions.analytics.track, config.analytics.eventTypes.form.open);
      }
    },
    showResultMessage(msg) {
      if (msg) {
        this.sending = false;
        this.showFormResult();
      }
    },
  },
  computed: {
    ...mapGetters({
      errorMessage: 'contactFormError',
      resultMessage: 'contactFormResult',
      validationErrors: 'validationErrors',
    }),
  },
  watch: {
    resultMessage(msg) {
      this.showResultMessage(msg);
    },
    errorMessage(msg) {
      this.showResultMessage(msg);
    },
    'formData.requestType'(actionId) {
      this.messagePlaceholder = _.find(this.requestTypes, { id: actionId }).message;
      this.detailedQuestion = _.find(this.requestTypes, { id: actionId }).question;
      void this.$store.dispatch(actions.analytics.append, { eVar56: this.actionMapping[actionId] });
    },
  },
  components: {
    FormInput,
    FormButton: defineAsyncComponent(() => import('@/app/components/form-button/form-button.vue')),
  },
  mounted() {
    this.id = this.$.uid.toString();
    this.formData.requestType = this.requestTypes[0].id;
    this.sendContactAnalytics(this.analytics);
    void this.$store.dispatch(actions.analytics.append, {
      eVar57: this.office.id,
      eVar75: this.agent.agentId,
    });

    this.$bus.$on(
      'dialogueClosed',
      (message) => {
        this.sendClosingAnalytics(message);
      },
      this
    );
  },
  beforeUnmount() {
    this.resetForm();
    this.$bus.$off('dialogueClosed', this);
  },
};
</script>
